<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: ResearchPage                                          |
| Section: views/info                                              |
| Purpose: Shows automatically derived research-in-progress trees. |
+------------------------------------------------------------------+
-->
<template>
    <div class="ResearchPage_wrapper wrapper">
        <header class="research-header">
            <h1>{{ $t('researchPage.title') }}</h1>
            <p class="subtitle">{{ $t('researchPage.subtitle') }}</p>
            <p v-if="!loading && !error && groups.length" class="research-summary">
                {{ getSummary(researchData.treeCount, researchData.personCount) }}
            </p>

            <div v-if="groups.length" class="category_buttons" :aria-label="$t('researchPage.familyFilter')">
                <button type="button" @click="resetFilter" :class="{ active: !selectedCategory }">
                    {{ $t('researchPage.allFamilies') }}
                </button>
                <button v-for="group in groups" :key="group.key" type="button"
                    @click="filterByFamily(group.key)" :class="{ active: selectedCategory === group.key }">
                    {{ getGroupLabel(group) }}
                    <span class="category-count">{{ group.trees.length }}</span>
                </button>
            </div>
        </header>

        <div v-if="loading" class="research-state" aria-live="polite">
            <LoadingSpinner />
            <p>{{ $t('researchPage.loading') }}</p>
        </div>

        <div v-else-if="error" class="research-state research-state--error" role="alert">
            <h2>{{ $t('researchPage.errorTitle') }}</h2>
            <p>{{ $t('researchPage.error') }}</p>
            <button type="button" @click="loadResearchGroups">{{ $t('researchPage.retry') }}</button>
        </div>

        <div v-else-if="!filteredGroups.length" class="research-state">
            <p>{{ $t('researchPage.empty') }}</p>
        </div>

        <section v-for="group in filteredGroups" :key="group.key" class="PersonCardGroup_wrapper">
            <div class="research-group-heading">
                <h2>{{ getGroupLabel(group) }}</h2>
                <p>{{ getSummary(group.trees.length, group.personCount, true) }}</p>
            </div>
            <div class="PersonCardGroup">
                <div v-for="tree in group.trees" :key="tree.id" class="research-lead-card">
                    <PersonCard :photoSrc="getProfileImageSrc(getLeadPerson(tree))"
                        :name="getPersonName(getLeadPerson(tree))"
                        :years="getPersonYears(getLeadPerson(tree))"
                        :gender="getPersonGender(getLeadPerson(tree))" href="javascript:void(0)"
                        :personID="getLeadPerson(tree)?.personID" :firstname="getLeadPerson(tree)?.firstname || ''"
                        :lastname="getLeadPerson(tree)?.lastname || ''" :living="getLeadPerson(tree)?.living || 0"
                        :isSmall="true" @click="openTreePanel(tree, group, $event)"
                        @keydown.enter="openTreePanel(tree, group, $event)"
                        @keydown.space.prevent="openTreePanel(tree, group, $event)"
                        :class="{ 'PersonCard_small_guest': isPrivatePerson(getLeadPerson(tree)) }" />
                    <span class="research-lead-card__count">
                        {{ getTreePeopleCount(tree.memberCount) }}
                    </span>
                </div>
            </div>
        </section>

        <ResearchTreePanel :visible="treePanelVisible" :tree="selectedTree"
            :groupLabel="selectedGroupLabel" @close="closeTreePanel" />
    </div>
    <ContactCta />
</template>

<script>
import ContactCta from '@/components/ContactCta.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import PersonCard from '@/atoms/PersonCard.vue';
import ResearchTreePanel from '@/components/ResearchTreePanel.vue';
import researchOverrides from '@/data/getResearchFamilies/researchOverrides.json';
import { fetchAllPeopleGraphStaticFile } from '@/api/apiService';
import { useAuth } from '@/composables/useAuth';
import { formatYears } from '@/utils/dateUtils.js';
import { getDefaultPhoto } from '@/utils/photoUtils';
import { isPrivateLivingPerson } from '@/utils/personPrivacy';
import { buildResearchGroups, validateResearchGroupsSnapshot } from '@/utils/researchGroups.mjs';
import { fetchRuntimeJson } from '@/utils/runtimeJson';
import { applyGenericSeo } from '@/utils/seoUtils';

export default {
    name: 'ResearchPage',
    components: {
        ContactCta,
        LoadingSpinner,
        PersonCard,
        ResearchTreePanel,
    },
    setup() {
        const { authToken } = useAuth();
        return { authToken };
    },
    data() {
        return {
            researchData: { personCount: 0, treeCount: 0, groups: [] },
            selectedCategory: null,
            selectedTree: null,
            selectedGroupLabel: '',
            treePanelVisible: false,
            panelTrigger: null,
            loading: true,
            error: null,
        };
    },
    computed: {
        groups() {
            return this.researchData.groups || [];
        },
        filteredGroups() {
            if (!this.selectedCategory) return this.groups;
            return this.groups.filter(group => group.key === this.selectedCategory);
        },
    },
    async created() {
        document.title = this.$t('researchPage.seoTitle');
        await this.loadResearchGroups();
    },
    mounted() {
        const title = this.$t('researchPage.seoTitle');
        const description = this.$t('researchPage.seoDescription');
        const image = 'https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg';
        applyGenericSeo({ title, description, image, url: window.location.href }, { includeTwitter: true });
    },
    methods: {
        formatYears,
        async loadResearchGroups() {
            this.loading = true;
            this.error = null;
            try {
                let snapshot = await fetchRuntimeJson('/data/research-groups.json', null);
                try {
                    if (snapshot) validateResearchGroupsSnapshot(snapshot);
                } catch (validationError) {
                    console.warn('Invalid research groups snapshot; rebuilding from people graph.', validationError);
                    snapshot = null;
                }

                if (!snapshot) {
                    const graph = await fetchAllPeopleGraphStaticFile();
                    snapshot = buildResearchGroups(graph, researchOverrides);
                }
                this.researchData = validateResearchGroupsSnapshot(snapshot);
                if (this.selectedCategory && !this.groups.some(group => group.key === this.selectedCategory)) {
                    this.selectedCategory = null;
                }
            } catch (error) {
                console.error('Could not load research groups:', error);
                this.error = error;
                this.researchData = { personCount: 0, treeCount: 0, groups: [] };
            } finally {
                this.loading = false;
            }
        },
        getGroupLabel(group) {
            return group.key === 'unknown'
                ? this.$t('researchPage.unknownFamily')
                : (group.label || group.key);
        },
        getTreeCount(count) {
            const key = count === 1 ? 'researchPage.treeCountOne' : 'researchPage.treeCountMany';
            return this.$t(key, { count });
        },
        getPeopleCount(count) {
            const key = count === 1 ? 'researchPage.peopleCountOne' : 'researchPage.peopleCountMany';
            return this.$t(key, { count });
        },
        getSummary(treeCount, personCount, compact = false) {
            const trees = this.getTreeCount(treeCount);
            const people = this.getPeopleCount(personCount);
            return compact
                ? `${trees} · ${people}`
                : this.$t('researchPage.summary', { people, trees });
        },
        getTreePeopleCount(count) {
            const key = count === 1
                ? 'researchPage.treePeopleCountOne'
                : 'researchPage.treePeopleCountMany';
            return this.$t(key, { count });
        },
        getLeadPerson(tree) {
            return tree?.members?.find(person => person.personID === tree.leadPersonId) || null;
        },
        getPersonName(person) {
            if (!person) return this.$t('researchPage.panel.unknownPerson');
            return `${person.firstname || ''} ${person.lastname || ''}`.trim()
                || this.$t('researchPage.panel.unknownPerson');
        },
        getPersonYears(person) {
            if (!person) return '';
            return formatYears(
                person.birthdatetr,
                person.altbirthdatetr,
                person.deathdatetr,
                person.burialdatetr,
                person.living,
            );
        },
        getProfileImageSrc(person) {
            return person?.photoPath || getDefaultPhoto(person?.sex);
        },
        getPersonGender(person) {
            return ['M', 'F'].includes(person?.sex) ? person.sex : 'unknown';
        },
        isPrivatePerson(person) {
            return person ? isPrivateLivingPerson(person, this.authToken) : false;
        },
        filterByFamily(groupKey) {
            this.selectedCategory = groupKey;
        },
        resetFilter() {
            this.selectedCategory = null;
        },
        openTreePanel(tree, group, event) {
            this.panelTrigger = event?.currentTarget || null;
            this.selectedTree = tree;
            this.selectedGroupLabel = this.getGroupLabel(group);
            this.treePanelVisible = true;
        },
        closeTreePanel() {
            this.treePanelVisible = false;
            const trigger = this.panelTrigger;
            window.setTimeout(() => {
                this.selectedTree = null;
                this.selectedGroupLabel = '';
                this.panelTrigger = null;
                trigger?.focus?.();
            }, 300);
        },
    },
};
</script>

<style scoped>
.ResearchPage_wrapper {
    min-height: calc(100vh - 106px - 516px);
    padding: var(--page-padding);
    display: flex;
    flex-direction: column;
    gap: 72px;
}

.research-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

h1,
.subtitle,
.research-summary { text-align: left; }
.subtitle { margin: 16px 0 0; }

.research-summary {
    margin: 16px 0 0;
    color: #666;
    font-size: 14px;
}

.category_buttons { display: none; }

.category-count {
    display: inline-flex;
    min-width: 20px;
    height: 20px;
    margin-left: 6px;
    padding: 0 6px;
    align-items: center;
    justify-content: center;
    border-radius: var(--border-radius-pill);
    background: rgba(0, 0, 0, 0.08);
    font-size: 12px;
}

.PersonCardGroup_wrapper { padding: 0; }

.research-group-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 20px;
}

.research-group-heading h2,
.research-group-heading p { margin: 0; }
.research-group-heading p { color: #666; font-size: 14px; }
.research-lead-card { position: relative; }

.research-lead-card {
    width: 100%;
    max-width: calc(33% - 13px);
    flex: none;
}

.research-lead-card :deep(.PersonCard) {
    width: 100% !important;
    max-width: none !important;
    height: 100%;
}

.research-lead-card__count {
    position: absolute;
    right: 10px;
    bottom: 8px;
    left: 10px;
    color: #666;
    font-size: 11px;
    text-align: center;
    pointer-events: none;
}

.research-state {
    min-height: 260px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    text-align: center;
}

.research-state p,
.research-state h2 { margin: 0; }
.research-state--error { color: #8b1e1e; }

.research-state button {
    padding: 10px 18px;
    cursor: pointer;
    background: var(--brand-color-1);
    color: white;
    border: 0;
    border-radius: var(--border-radius-md);
}

@media (max-width: 860px) {
    .ResearchPage_wrapper { padding: var(--page-padding-mobile); gap: 56px; }
    .research-group-heading { align-items: flex-start; flex-direction: column; gap: 6px; }
    .research-lead-card { max-width: 100%; }
}
</style>
